import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { auth } from '../../firebase';
import {
  ArrowLeft,
  Sun,
  Moon,
  Lock,
  Trash2,
  ShieldOff,
  Eye,
  EyeOff,
  AlertTriangle,
  Settings,
  LogOut,
  HelpCircle,
  UserCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

const CF_BASE = 'https://europe-west1-medaviceia.cloudfunctions.net';

const GestionComptePage = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  // --- Mot de passe ---
  const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });
  const [pwdLoading, setPwdLoading] = useState(false);

  // --- Révocation sessions ---
  const [revokeLoading, setRevokeLoading] = useState(false);

  // --- Suppression compte ---
  const [deleteStep, setDeleteStep] = useState(null); // null | 'confirm' | 'auth'
  const [deletePwd, setDeletePwd] = useState('');
  const [showDeletePwd, setShowDeletePwd] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const initials =
    user?.first_name && user?.last_name
      ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
      : 'JD';

  const prefix = user?.profile_type === 'medecin' ? 'Dr.' : 'IDE.';
  const displayName = `${prefix} ${user?.last_name || ''}`;

  // ── Changement de mot de passe ──────────────────────────
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwdForm.next !== pwdForm.confirm) {
      toast.error('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }
    if (pwdForm.next.length < 8) {
      toast.error('Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    setPwdLoading(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, pwdForm.current);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, pwdForm.next);
      toast.success('Mot de passe mis à jour avec succès.');
      setPwdForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        toast.error('Mot de passe actuel incorrect.');
      } else {
        toast.error('Erreur lors du changement de mot de passe.');
      }
    } finally {
      setPwdLoading(false);
    }
  };

  // ── Révocation de toutes les sessions ───────────────────
  const handleRevokeSessions = async () => {
    setRevokeLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${CF_BASE}/revokeUserSessions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Tous les autres appareils ont été déconnectés.');
    } catch {
      toast.error('Erreur lors de la révocation des sessions.');
    } finally {
      setRevokeLoading(false);
    }
  };

  // ── Suppression de compte ────────────────────────────────
  const handleDeleteAccount = async () => {
    if (!deletePwd) {
      toast.error('Veuillez saisir votre mot de passe pour confirmer.');
      return;
    }
    setDeleteLoading(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, deletePwd);
      await reauthenticateWithCredential(auth.currentUser, credential);

      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${CF_BASE}/deleteUserAccount`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();

      await logout();
      navigate('/auth/pro', { replace: true });
      toast.success('Votre compte a été supprimé.');
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        toast.error('Mot de passe incorrect. Suppression annulée.');
      } else {
        toast.error('Erreur lors de la suppression du compte.');
      }
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 transition-colors duration-200">

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-sm transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pro/infirmier')}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200 mr-1"
              aria-label="Retour"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </button>
            <img
              src="/RemaH.png"
              alt="Remadia Pro"
              className="w-10 h-10"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="text-xl font-bold text-[#003D82] dark:text-blue-400">Remadia</span>
            <span className="px-2 py-1 bg-teal-500/10 text-teal-600 dark:text-teal-400 text-xs font-semibold rounded-full border border-teal-500/20 dark:border-teal-500/30">
              PRO
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors duration-200"
              aria-label="Changer de thème"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-slate-600" />
              )}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200">
                  <div className="w-8 h-8 rounded-full bg-[#003D82] dark:bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                    {initials}
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-slate-700 dark:text-slate-200">
                    {displayName}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100">
                <DropdownMenuItem
                  onClick={() => navigate('/pro/infirmier/profil')}
                  className="dark:hover:bg-slate-700"
                >
                  <UserCircle className="w-4 h-4 mr-2" />
                  Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/pro/infirmier/compte')}
                  className="dark:hover:bg-slate-700"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Gestion du compte
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/pro/infirmier/faq')}
                  className="dark:hover:bg-slate-700"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Aide / FAQ
                </DropdownMenuItem>
                <DropdownMenuSeparator className="dark:bg-slate-700" />
                <DropdownMenuItem
                  onClick={async () => { await logout(); navigate('/auth/pro'); }}
                  className="text-destructive dark:text-red-400 dark:hover:bg-slate-700"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#003D82] to-[#0056B3] dark:from-slate-800 dark:to-teal-900 text-white py-10 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-2xl font-bold mb-1">Gestion du compte</h1>
          <p className="text-blue-100 dark:text-blue-200 text-sm">
            Sécurité et suppression de compte
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">

        {/* ── Sécurité ─────────────────────────────────────── */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 transition-colors duration-200">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-teal-500/10 dark:bg-teal-500/20 flex items-center justify-center">
              <Lock className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Sécurité</h2>
          </div>

          {/* Changement de mot de passe */}
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Changer le mot de passe
            </h3>

            {['current', 'next', 'confirm'].map((field) => {
              const labels = {
                current: 'Mot de passe actuel',
                next: 'Nouveau mot de passe',
                confirm: 'Confirmer le nouveau mot de passe',
              };
              return (
                <div key={field} className="relative">
                  <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide block mb-1">
                    {labels[field]}
                  </label>
                  <div className="relative">
                    <input
                      type={showPwd[field] ? 'text' : 'password'}
                      value={pwdForm[field]}
                      onChange={(e) => setPwdForm({ ...pwdForm, [field]: e.target.value })}
                      className="w-full text-sm bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 pr-10 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-colors duration-200"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd({ ...showPwd, [field]: !showPwd[field] })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    >
                      {showPwd[field] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              );
            })}

            <button
              type="submit"
              disabled={pwdLoading}
              className="w-full py-2.5 bg-teal-500 hover:bg-teal-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors duration-200"
            >
              {pwdLoading ? 'Mise à jour…' : 'Mettre à jour le mot de passe'}
            </button>
          </form>

          <div className="border-t border-slate-100 dark:border-slate-700 my-6" />

          {/* Révocation sessions */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">
              Déconnexion de tous les appareils
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Invalide toutes les sessions actives sur vos autres appareils. Votre session actuelle reste ouverte.
            </p>
            <button
              onClick={handleRevokeSessions}
              disabled={revokeLoading}
              className="flex items-center gap-2 px-4 py-2 border-2 border-slate-200 dark:border-slate-600 hover:border-teal-500 dark:hover:border-teal-500 text-slate-700 dark:text-slate-200 hover:text-teal-600 dark:hover:text-teal-400 text-sm font-semibold rounded-lg transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <ShieldOff className="w-4 h-4" />
              {revokeLoading ? 'Révocation…' : 'Déconnecter tous les appareils'}
            </button>
          </div>
        </div>

        {/* ── Compte ───────────────────────────────────────── */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-red-200 dark:border-red-900/50 transition-colors duration-200">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-500" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Compte</h2>
          </div>

          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
            La suppression est <strong>irréversible</strong>. Toutes vos données médicales, historiques et paramètres seront définitivement effacés.
          </p>

          <button
            onClick={() => setDeleteStep('confirm')}
            className="flex items-center gap-2 px-4 py-2 border-2 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 text-sm font-semibold rounded-lg transition-colors duration-200"
          >
            <Trash2 className="w-4 h-4" />
            Supprimer mon compte
          </button>
        </div>
      </div>

      {/* ── Modale suppression étape 1 ────────────────────── */}
      {deleteStep === 'confirm' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-700 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                Confirmer la suppression
              </h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
              Vous êtes sur le point de supprimer définitivement votre compte <strong>{user?.email}</strong> et toutes les données associées. Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteStep(null)}
                className="flex-1 py-2.5 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-sm font-semibold rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                Annuler
              </button>
              <button
                onClick={() => setDeleteStep('auth')}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-colors duration-200"
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale suppression étape 2 ────────────────────── */}
      {deleteStep === 'auth' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-700 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                Confirmer votre identité
              </h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              Saisissez votre mot de passe pour finaliser la suppression.
            </p>
            <div className="relative mb-6">
              <input
                type={showDeletePwd ? 'text' : 'password'}
                value={deletePwd}
                onChange={(e) => setDeletePwd(e.target.value)}
                placeholder="Votre mot de passe"
                className="w-full text-sm bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 pr-10 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500 transition-colors duration-200"
              />
              <button
                type="button"
                onClick={() => setShowDeletePwd(!showDeletePwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                {showDeletePwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setDeleteStep(null); setDeletePwd(''); }}
                disabled={deleteLoading}
                className="flex-1 py-2.5 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-sm font-semibold rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-200 disabled:opacity-60"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading || !deletePwd}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors duration-200"
              >
                {deleteLoading ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionComptePage;
